import {
  DEFAULT_WORLD_LAYOUT,
  clampTileX,
  clampTileY,
  normalizeWorldLayout,
  type WorldLayout,
} from '../game/worldLayout';
import type {
  FollowBehavior,
  FollowLostBehavior,
  FollowMovementConfig,
  FollowMovementTargetKind,
  MovementConfig,
  MovementMode,
  MovementSpeedProfile,
  NpcWaypoint,
  PatrolMovementConfig,
  PatrolPathMode,
  ScheduledFallbackMode,
  ScheduledMovementConfig,
  ScheduledTransitionSpeed,
  StationaryFacingDirection,
  StationaryIdleBehavior,
  StationaryMovementConfig,
  TerritorialAlertResponse,
  TerritorialMovementConfig,
  TerritorialPatrolStyle,
  WanderBias,
  WanderMovementConfig,
  WanderRestFrequency,
} from '../types/npc';

const MOVEMENT_MODES: MovementMode[] = [
  'stationary',
  'patrol',
  'wander',
  'territorial',
  'follow',
  'scheduled',
];
const SPEED_PROFILES: MovementSpeedProfile[] = ['slow', 'normal', 'fast', 'varied'];
const STATIONARY_FACING_DIRECTIONS: StationaryFacingDirection[] = [
  'north',
  'south',
  'east',
  'west',
  'random',
];
const STATIONARY_IDLE_BEHAVIORS: StationaryIdleBehavior[] = ['still', 'look-around', 'fidget'];
const PATROL_PATH_MODES: PatrolPathMode[] = ['loop', 'pingpong', 'one-shot'];
const WANDER_BIASES: WanderBias[] = [
  'uniform',
  'center-weighted',
  'edge-weighted',
  'poi-attracted',
];
const WANDER_REST_FREQUENCIES: WanderRestFrequency[] = ['rarely', 'sometimes', 'often'];
const TERRITORIAL_PATROL_STYLES: TerritorialPatrolStyle[] = [
  'perimeter',
  'interior-sweep',
  'mixed',
];
const TERRITORIAL_ALERT_RESPONSES: TerritorialAlertResponse[] = ['approach', 'face', 'ignore'];
const FOLLOW_BEHAVIORS: FollowBehavior[] = ['shadow', 'orbit', 'lead'];
const FOLLOW_LOST_BEHAVIORS: FollowLostBehavior[] = ['wait', 'return-to-spawn', 'wander'];
const FOLLOW_TARGET_KINDS: FollowMovementTargetKind[] = ['placement', 'zone', 'door', 'sign'];
const SCHEDULED_TRANSITION_SPEEDS: ScheduledTransitionSpeed[] = ['walk', 'hurry'];
const SCHEDULED_FALLBACK_MODES: ScheduledFallbackMode[] = ['stationary', 'wander'];

export const SUPPORTED_RUNTIME_MOVEMENT_MODES: MovementMode[] = ['stationary', 'patrol', 'wander'];

export const DEFAULT_STATIONARY_MOVEMENT_CONFIG: StationaryMovementConfig = {
  facingDirection: 'south',
  idleBehavior: 'look-around',
};

export const DEFAULT_PATROL_MOVEMENT_CONFIG: PatrolMovementConfig = {
  waypoints: [],
  pathMode: 'loop',
  pauseAtWaypoints: true,
  pauseDurationMin: 1,
  pauseDurationMax: 3,
};

export const DEFAULT_WANDER_MOVEMENT_CONFIG: WanderMovementConfig = {
  radius: 6,
  bias: 'center-weighted',
  restFrequency: 'sometimes',
  restDurationMin: 1,
  restDurationMax: 3,
};

export const DEFAULT_TERRITORIAL_MOVEMENT_CONFIG: TerritorialMovementConfig = {
  bounds: { x: 0, y: 0, width: 6, height: 6 },
  patrolStyle: 'perimeter',
  alertRadius: 6,
  alertResponse: 'face',
};

export const DEFAULT_FOLLOW_MOVEMENT_CONFIG: FollowMovementConfig = {
  target: null,
  distance: 2,
  behavior: 'shadow',
  lostBehavior: 'wait',
};

export const DEFAULT_SCHEDULED_MOVEMENT_CONFIG: ScheduledMovementConfig = {
  schedule: [],
  transitionSpeed: 'walk',
  fallbackMode: 'stationary',
};

export const DEFAULT_MOVEMENT_CONFIG: MovementConfig = {
  mode: 'stationary',
  speedProfile: 'normal',
  stationary: { ...DEFAULT_STATIONARY_MOVEMENT_CONFIG },
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const clampNumber = (
  value: unknown,
  minimum: number,
  maximum: number,
  fallback: number,
): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.max(minimum, Math.min(maximum, parsed));
};

const clampInteger = (value: unknown, minimum: number, maximum: number, fallback: number): number =>
  Math.round(clampNumber(value, minimum, maximum, fallback));

const clampTileCoordinate = (
  value: unknown,
  axis: 'x' | 'y',
  layout: WorldLayout,
): number | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return axis === 'x' ? clampTileX(parsed, layout) : clampTileY(parsed, layout);
};

const pickEnum = <T extends string>(options: T[], value: unknown, fallback: T): T =>
  options.includes(value as T) ? (value as T) : fallback;

export const cloneWaypoint = (value: NpcWaypoint): NpcWaypoint => ({ x: value.x, y: value.y });

export const cloneWaypointList = (value: NpcWaypoint[]): NpcWaypoint[] => value.map(cloneWaypoint);

export const sanitizeWaypoint = (
  value: unknown,
  layout: WorldLayout = DEFAULT_WORLD_LAYOUT,
): NpcWaypoint | null => {
  const candidate = asRecord(value);
  if (!candidate) {
    return null;
  }

  const x = clampTileCoordinate(candidate.x, 'x', layout);
  const y = clampTileCoordinate(candidate.y, 'y', layout);
  if (x === null || y === null) {
    return null;
  }

  return { x, y };
};

export const sanitizeWaypointList = (
  value: unknown,
  layout: WorldLayout = DEFAULT_WORLD_LAYOUT,
): NpcWaypoint[] =>
  Array.isArray(value)
    ? value
        .map((entry) => sanitizeWaypoint(entry, layout))
        .filter((entry): entry is NpcWaypoint => Boolean(entry))
    : [];

const sanitizeStationaryConfig = (value: unknown): StationaryMovementConfig => {
  const candidate = asRecord(value);
  if (!candidate) {
    return { ...DEFAULT_STATIONARY_MOVEMENT_CONFIG };
  }

  return {
    facingDirection: pickEnum(
      STATIONARY_FACING_DIRECTIONS,
      candidate.facingDirection,
      DEFAULT_STATIONARY_MOVEMENT_CONFIG.facingDirection,
    ),
    idleBehavior: pickEnum(
      STATIONARY_IDLE_BEHAVIORS,
      candidate.idleBehavior,
      DEFAULT_STATIONARY_MOVEMENT_CONFIG.idleBehavior,
    ),
  };
};

const sanitizePatrolConfig = (
  value: unknown,
  fallbackWaypoints: NpcWaypoint[],
  layout: WorldLayout,
): PatrolMovementConfig => {
  const candidate = asRecord(value);
  const waypoints = sanitizeWaypointList(candidate?.waypoints, layout);
  const pauseDurationMin = clampNumber(
    candidate?.pauseDurationMin,
    0.5,
    10,
    DEFAULT_PATROL_MOVEMENT_CONFIG.pauseDurationMin,
  );
  const pauseDurationMax = clampNumber(
    candidate?.pauseDurationMax,
    pauseDurationMin,
    10,
    Math.max(DEFAULT_PATROL_MOVEMENT_CONFIG.pauseDurationMax, pauseDurationMin),
  );

  return {
    waypoints: cloneWaypointList(waypoints.length > 0 ? waypoints : fallbackWaypoints),
    pathMode: pickEnum(
      PATROL_PATH_MODES,
      candidate?.pathMode,
      DEFAULT_PATROL_MOVEMENT_CONFIG.pathMode,
    ),
    pauseAtWaypoints:
      typeof candidate?.pauseAtWaypoints === 'boolean'
        ? candidate.pauseAtWaypoints
        : DEFAULT_PATROL_MOVEMENT_CONFIG.pauseAtWaypoints,
    pauseDurationMin,
    pauseDurationMax,
  };
};

const sanitizeWanderConfig = (value: unknown): WanderMovementConfig => {
  const candidate = asRecord(value);
  const restDurationMin = clampNumber(
    candidate?.restDurationMin,
    0.5,
    10,
    DEFAULT_WANDER_MOVEMENT_CONFIG.restDurationMin,
  );
  const restDurationMax = clampNumber(
    candidate?.restDurationMax,
    restDurationMin,
    10,
    Math.max(DEFAULT_WANDER_MOVEMENT_CONFIG.restDurationMax, restDurationMin),
  );

  return {
    radius: clampInteger(candidate?.radius, 3, 20, DEFAULT_WANDER_MOVEMENT_CONFIG.radius),
    bias: pickEnum(WANDER_BIASES, candidate?.bias, DEFAULT_WANDER_MOVEMENT_CONFIG.bias),
    restFrequency: pickEnum(
      WANDER_REST_FREQUENCIES,
      candidate?.restFrequency,
      DEFAULT_WANDER_MOVEMENT_CONFIG.restFrequency,
    ),
    restDurationMin,
    restDurationMax,
  };
};

const sanitizeTerritorialConfig = (
  value: unknown,
  layout: WorldLayout,
): TerritorialMovementConfig => {
  const candidate = asRecord(value);
  const bounds = asRecord(candidate?.bounds);
  const maxX = Math.max(0, layout.tileWidth - 1);
  const maxY = Math.max(0, layout.tileHeight - 1);

  return {
    bounds: {
      x: clampInteger(bounds?.x, 0, maxX, DEFAULT_TERRITORIAL_MOVEMENT_CONFIG.bounds.x),
      y: clampInteger(bounds?.y, 0, maxY, DEFAULT_TERRITORIAL_MOVEMENT_CONFIG.bounds.y),
      width: clampInteger(
        bounds?.width,
        1,
        layout.tileWidth,
        DEFAULT_TERRITORIAL_MOVEMENT_CONFIG.bounds.width,
      ),
      height: clampInteger(
        bounds?.height,
        1,
        layout.tileHeight,
        DEFAULT_TERRITORIAL_MOVEMENT_CONFIG.bounds.height,
      ),
    },
    patrolStyle: pickEnum(
      TERRITORIAL_PATROL_STYLES,
      candidate?.patrolStyle,
      DEFAULT_TERRITORIAL_MOVEMENT_CONFIG.patrolStyle,
    ),
    alertRadius: clampInteger(
      candidate?.alertRadius,
      1,
      20,
      DEFAULT_TERRITORIAL_MOVEMENT_CONFIG.alertRadius,
    ),
    alertResponse: pickEnum(
      TERRITORIAL_ALERT_RESPONSES,
      candidate?.alertResponse,
      DEFAULT_TERRITORIAL_MOVEMENT_CONFIG.alertResponse,
    ),
  };
};

const sanitizeFollowConfig = (value: unknown): FollowMovementConfig => {
  const candidate = asRecord(value);
  const targetCandidate = asRecord(candidate?.target);
  const targetKind = pickEnum(FOLLOW_TARGET_KINDS, targetCandidate?.kind, 'placement');
  const targetId = String(targetCandidate?.id ?? candidate?.targetId ?? '').trim();

  return {
    target: targetId ? { kind: targetKind, id: targetId } : null,
    distance: clampInteger(candidate?.distance, 1, 8, DEFAULT_FOLLOW_MOVEMENT_CONFIG.distance),
    behavior: pickEnum(
      FOLLOW_BEHAVIORS,
      candidate?.behavior,
      DEFAULT_FOLLOW_MOVEMENT_CONFIG.behavior,
    ),
    lostBehavior: pickEnum(
      FOLLOW_LOST_BEHAVIORS,
      candidate?.lostBehavior,
      DEFAULT_FOLLOW_MOVEMENT_CONFIG.lostBehavior,
    ),
  };
};

const sanitizeScheduledConfig = (value: unknown, layout: WorldLayout): ScheduledMovementConfig => {
  const candidate = asRecord(value);
  const rawSchedule = Array.isArray(candidate?.schedule) ? candidate.schedule : [];

  return {
    schedule: rawSchedule
      .map((entry) => {
        const scheduleEntry = asRecord(entry);
        if (!scheduleEntry) {
          return null;
        }
        const location = sanitizeWaypoint(scheduleEntry.location, layout);
        if (!location) {
          return null;
        }
        return {
          startHour: clampInteger(scheduleEntry.startHour, 0, 23, 0),
          endHour: clampInteger(scheduleEntry.endHour, 0, 23, 0),
          location,
          activity: String(scheduleEntry.activity ?? '')
            .trim()
            .slice(0, 80),
        };
      })
      .filter(
        (
          entry,
        ): entry is {
          startHour: number;
          endHour: number;
          location: NpcWaypoint;
          activity: string;
        } => Boolean(entry),
      ),
    transitionSpeed: pickEnum(
      SCHEDULED_TRANSITION_SPEEDS,
      candidate?.transitionSpeed,
      DEFAULT_SCHEDULED_MOVEMENT_CONFIG.transitionSpeed,
    ),
    fallbackMode: pickEnum(
      SCHEDULED_FALLBACK_MODES,
      candidate?.fallbackMode,
      DEFAULT_SCHEDULED_MOVEMENT_CONFIG.fallbackMode,
    ),
  };
};

export const movementConfigFromWaypointPath = (waypointPath: NpcWaypoint[]): MovementConfig => {
  const sanitizedWaypoints = cloneWaypointList(waypointPath);
  if (sanitizedWaypoints.length > 0) {
    return {
      mode: 'patrol',
      speedProfile: 'varied',
      patrol: {
        ...DEFAULT_PATROL_MOVEMENT_CONFIG,
        waypoints: sanitizedWaypoints,
      },
    };
  }

  return {
    ...DEFAULT_MOVEMENT_CONFIG,
    stationary: { ...DEFAULT_STATIONARY_MOVEMENT_CONFIG },
  };
};

export const normalizeMovementConfig = (
  value: MovementConfig | null | undefined,
  legacyWaypoints: NpcWaypoint[] = [],
  layout: WorldLayout = DEFAULT_WORLD_LAYOUT,
): MovementConfig => parseMovementConfigCandidate(value, legacyWaypoints, layout);

export const parseMovementConfigCandidate = (
  value: unknown,
  legacyWaypoints: NpcWaypoint[] = [],
  layout: WorldLayout = DEFAULT_WORLD_LAYOUT,
): MovementConfig => {
  const normalizedLayout = normalizeWorldLayout(layout);
  const fallback = movementConfigFromWaypointPath(legacyWaypoints);
  const candidate = asRecord(value);
  if (!candidate) {
    return fallback;
  }

  const mode = pickEnum(MOVEMENT_MODES, candidate.mode, fallback.mode);
  const speedProfile = pickEnum(SPEED_PROFILES, candidate.speedProfile, fallback.speedProfile);
  const nextConfig: MovementConfig = {
    mode,
    speedProfile,
  };

  switch (mode) {
    case 'stationary':
      nextConfig.stationary = sanitizeStationaryConfig(candidate.stationary);
      break;
    case 'patrol':
      nextConfig.patrol = sanitizePatrolConfig(candidate.patrol, legacyWaypoints, normalizedLayout);
      break;
    case 'wander':
      nextConfig.wander = sanitizeWanderConfig(candidate.wander);
      break;
    case 'territorial':
      nextConfig.territorial = sanitizeTerritorialConfig(candidate.territorial, normalizedLayout);
      break;
    case 'follow':
      nextConfig.follow = sanitizeFollowConfig(candidate.follow);
      break;
    case 'scheduled':
      nextConfig.scheduled = sanitizeScheduledConfig(candidate.scheduled, normalizedLayout);
      break;
  }

  return nextConfig;
};

export const cloneMovementConfig = (
  value: MovementConfig,
  layout: WorldLayout = DEFAULT_WORLD_LAYOUT,
): MovementConfig => parseMovementConfigCandidate(value, toLegacyWaypointPath(value), layout);

export const toLegacyWaypointPath = (
  movementConfig: MovementConfig | null | undefined,
  fallbackWaypoints: NpcWaypoint[] = [],
): NpcWaypoint[] => {
  const resolved = normalizeMovementConfig(movementConfig, fallbackWaypoints);
  if (resolved.mode !== 'patrol') {
    return [];
  }
  return cloneWaypointList(resolved.patrol?.waypoints ?? fallbackWaypoints);
};

export const isSupportedRuntimeMovementMode = (mode: MovementMode): boolean =>
  SUPPORTED_RUNTIME_MOVEMENT_MODES.includes(mode);

export const getMovementSpeedMultiplier = (speedProfile: MovementSpeedProfile): number => {
  switch (speedProfile) {
    case 'slow':
      return 0.8;
    case 'fast':
      return 1.25;
    case 'varied':
      return 1.05;
    case 'normal':
    default:
      return 1;
  }
};

export const getWanderRestChance = (restFrequency: WanderRestFrequency): number => {
  switch (restFrequency) {
    case 'rarely':
      return 0.2;
    case 'often':
      return 0.8;
    case 'sometimes':
    default:
      return 0.5;
  }
};
